import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { TenantInput, TenantProfile } from '../../../models/userManagement';

const MODEL_TYPES = ['SAVINGS', 'WASTE_MGT', 'REWORLD_WST_MGT', 'SLOTTED', 'SID', 'PRY', 'GEMBA_SLOTTED'];

interface TenantDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: TenantInput) => Promise<void> | void;
  initialValues?: TenantProfile;
}

const defaultValues: TenantInput = {
  id: '',
  name: '',
  description: '',
  allowedModelTypes: ['SAVINGS'],
  defaultModelType: 'SAVINGS',
  active: true,
};

const TenantDialog: React.FC<TenantDialogProps> = ({ open, onClose, onSubmit, initialValues }) => {
  const [values, setValues] = useState<TenantInput>(defaultValues);
  const [submitting, setSubmitting] = useState(false);

  const validationErrors = useMemo(() => {
    const errors: Partial<Record<keyof TenantInput, string>> & { allowedModelTypes?: string; defaultModelType?: string } = {};
    if (!values.id.trim()) {
      errors.id = 'Tenant ID is required';
    }
    if (!values.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!values.allowedModelTypes.length) {
      errors.allowedModelTypes = 'Select at least one model type';
    }
    if (!values.defaultModelType) {
      errors.defaultModelType = 'Select a default model type';
    } else if (values.allowedModelTypes.length && !values.allowedModelTypes.includes(values.defaultModelType)) {
      errors.defaultModelType = 'Default model type must be part of allowed list';
    }
    return errors;
  }, [values]);

  const isValid = useMemo(() => Object.values(validationErrors).every((msg) => !msg), [validationErrors]);

  useEffect(() => {
    if (initialValues) {
      setValues({
        id: initialValues.id,
        name: initialValues.name,
        description: initialValues.description,
        allowedModelTypes: initialValues.allowedModelTypes,
        defaultModelType: initialValues.defaultModelType,
        active: initialValues.active,
      });
    } else {
      setValues(defaultValues);
    }
  }, [initialValues, open]);

  const isEditMode = Boolean(initialValues);

  const validModelTypes = useMemo(() => {
    if (!values.allowedModelTypes.includes(values.defaultModelType)) {
      const [first] = values.allowedModelTypes;
      return { ...values, defaultModelType: first || 'SAVINGS' };
    }
    return values;
  }, [values]);

  useEffect(() => {
    if (validModelTypes !== values) {
      setValues(validModelTypes);
    }
  }, [validModelTypes, values]);

  const handleChange = (field: keyof TenantInput, value: any) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isValid) {
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(values);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEditMode ? 'Edit Tenant' : 'Create Tenant'}</DialogTitle>
      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Tenant ID"
              value={values.id}
              onChange={(event) => handleChange('id', event.target.value)}
              disabled={isEditMode}
              required
              error={Boolean(validationErrors.id)}
              helperText={validationErrors.id}
            />
            <TextField
              label="Name"
              value={values.name}
              onChange={(event) => handleChange('name', event.target.value)}
              required
              error={Boolean(validationErrors.name)}
              helperText={validationErrors.name}
            />
            <TextField
              label="Description"
              value={values.description}
              onChange={(event) => handleChange('description', event.target.value)}
              multiline
              minRows={2}
            />

            <FormControl fullWidth error={Boolean(validationErrors.allowedModelTypes)}>
              <InputLabel id="allowed-model-types-label">Allowed Model Types</InputLabel>
              <Select
                labelId="allowed-model-types-label"
                multiple
                value={values.allowedModelTypes}
                label="Allowed Model Types"
                onChange={(event) => handleChange('allowedModelTypes', event.target.value as string[])}
              >
                {MODEL_TYPES.map((model) => (
                  <MenuItem key={model} value={model}>
                    {model}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {validationErrors.allowedModelTypes || 'Select at least one model type'}
              </FormHelperText>
            </FormControl>

            <FormControl fullWidth error={Boolean(validationErrors.defaultModelType)}>
              <InputLabel id="default-model-type-label">Default Model Type</InputLabel>
              <Select
                labelId="default-model-type-label"
                value={values.defaultModelType}
                label="Default Model Type"
                onChange={(event) => handleChange('defaultModelType', event.target.value)}
              >
                {(values.allowedModelTypes.length ? values.allowedModelTypes : MODEL_TYPES).map((model) => (
                  <MenuItem key={model} value={model}>
                    {model}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {validationErrors.defaultModelType || 'Choose a default model type'}
              </FormHelperText>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={submitting || !isValid}>
            {isEditMode ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default TenantDialog;
